import type { HelperChecklistProof } from '../services/helper/helperChecklistDetailsService'

/**
 * Debug-bypass proof + activity store. Backed by window-scoped globals so all
 * Vite module instances see the same data. Triggers a `debug-proof-store`
 * CustomEvent on mutation so live subscribers (subscribeChecklistProofs) can
 * re-render.
 */

const PROOFS_KEY = '__DEBUG_PROOFS_BY_ITEM__'
const ACTIVITY_KEY = '__DEBUG_WORKSPACE_ACTIVITY__'
const STORE_EVENT = 'debug-proof-store'

export interface DebugActivityEntry {
  id: string
  workspaceId: string
  actorId: string
  actorName: string
  action: string
  activityType: string
  targetId: string
  targetTitle: string
  summary: string
  createdAt: Date
}

const getProofsMap = (): Map<string, HelperChecklistProof[]> => {
  if (typeof window === 'undefined') return new Map()
  const existing = (window as unknown as Record<string, unknown>)[PROOFS_KEY]
  if (existing instanceof Map) return existing as Map<string, HelperChecklistProof[]>
  const created = new Map<string, HelperChecklistProof[]>()
  ;(window as unknown as Record<string, unknown>)[PROOFS_KEY] = created
  return created
}

const getActivityList = (): DebugActivityEntry[] => {
  if (typeof window === 'undefined') return []
  const existing = (window as unknown as Record<string, unknown>)[ACTIVITY_KEY]
  if (Array.isArray(existing)) return existing as DebugActivityEntry[]
  const created: DebugActivityEntry[] = []
  ;(window as unknown as Record<string, unknown>)[ACTIVITY_KEY] = created
  return created
}

const emitChange = (): void => {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(STORE_EVENT))
}

export const subscribeDebugProofStore = (callback: () => void): (() => void) => {
  if (typeof window === 'undefined') return () => {}
  const handler = () => callback()
  window.addEventListener(STORE_EVENT, handler)
  return () => window.removeEventListener(STORE_EVENT, handler)
}

export const getDebugProofsForItem = (itemId: string): HelperChecklistProof[] =>
  getProofsMap().get(itemId) ?? []

export const recordDebugProof = (params: {
  workspaceId: string
  itemId: string
  itemTitle: string
  actorId: string
  actorName: string
  file: File
}): HelperChecklistProof => {
  const map = getProofsMap()
  const id = `debug-proof-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  const fileUrl = URL.createObjectURL(params.file)
  const proof: HelperChecklistProof = {
    id,
    fileUrl,
    fileName: params.file.name,
    contentType: params.file.type || 'application/octet-stream',
    storagePath: id,
    uploadedById: params.actorId,
    uploadedByName: params.actorName,
    uploadedAt: Date.now(),
  }
  const existing = map.get(params.itemId) ?? []
  map.set(params.itemId, [...existing, proof])

  const activity = getActivityList()
  activity.push({
    id: `debug-act-${id}`,
    workspaceId: params.workspaceId,
    actorId: params.actorId,
    actorName: params.actorName,
    action: 'PROOF_UPLOADED',
    activityType: 'CHECKLIST_PROOF',
    targetId: params.itemId,
    targetTitle: params.itemTitle,
    summary: `${params.actorName} attached proof "${params.file.name}"`,
    createdAt: new Date(),
  })

  emitChange()
  return proof
}

export const recordDebugLocationCheckIn = (params: {
  workspaceId: string
  itemId: string
  itemTitle: string
  actorId: string
  actorName: string
  coords: GeolocationCoordinates
}): void => {
  const activity = getActivityList()
  activity.push({
    id: `debug-act-loc-${Date.now()}`,
    workspaceId: params.workspaceId,
    actorId: params.actorId,
    actorName: params.actorName,
    action: 'LOCATION_CHECK_IN',
    activityType: 'CHECKLIST_LOCATION',
    targetId: params.itemId,
    targetTitle: params.itemTitle,
    summary: `${params.actorName} checked in at ${params.coords.latitude.toFixed(4)}, ${params.coords.longitude.toFixed(4)}`,
    createdAt: new Date(),
  })
  emitChange()
}

export const getDebugWorkspaceActivity = (workspaceId: string): DebugActivityEntry[] =>
  getActivityList()
    .filter((entry) => entry.workspaceId === workspaceId)
    .slice()
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
